import type { AAMedia } from '@akross/artemis-ads-sdk';
import { artemisService } from '../services/artemis.service';
import { AnalyticsUtil, ANALYTICS_EVENTS } from '../utils/analytics.util';
import { logger } from '../utils/logger.util';

type VastPlayerResult = 'completed' | 'skipped' | 'error';

const SKIP_DELAY_SECONDS = 5;

export class VastPlayerPage {
  private overlay: HTMLElement | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private resolveCallback: ((result: VastPlayerResult) => void) | null = null;

  // VAST tracking state
  private trackingEvents: Record<string, string[]> = {};
  private impressionUrls: string[] = [];
  private errorUrls: string[] = [];
  private clickTrackingUrls: string[] = [];
  private clickThroughUrl: string | null = null;
  private quartilesFired = { first: false, mid: false, third: false, complete: false };
  private allAdsCompletedFired = false;
  private campaignId: string = '';
  private mediaId: string = '';

  /**
   * Mostra o player VAST como overlay fullscreen.
   * Retorna uma Promise que resolve quando o vídeo termina ou o usuário pula.
   */
  async show(media: AAMedia, campaignId: string): Promise<VastPlayerResult> {
    const vastUrl = (media as any).content?.video?.url || (media as any).content?.url;

    if (!vastUrl) {
      logger.warnWithContext('VastPlayerPage', 'VAST URL não encontrada na mídia');
      return 'error';
    }

    return new Promise(async (resolve) => {
      this.resolveCallback = resolve;
      this.campaignId = campaignId;
      this.mediaId = media.uuid;
      this.resetState();
      this.createOverlay();

      try {
        logger.debug('[VastPlayerPage] Buscando VAST:', vastUrl);
        const vastXml = await this.fetchVast(vastUrl);

        if (!vastXml) {
          this.finish('error');
          return;
        }

        this.extractTrackingEvents(vastXml);
        this.extractImpressionUrls(vastXml);
        this.extractErrorUrls(vastXml);
        this.extractClickInfo(vastXml);

        const videoUrl = this.extractMediaFileUrl(vastXml);
        if (!videoUrl) {
          logger.warnWithContext('VastPlayerPage', 'Nenhum MediaFile encontrado no VAST');
          this.finish('error');
          return;
        }

        logger.debug('[VastPlayerPage] MediaFile encontrado, iniciando reprodução');

        this.hideLoading();
        this.setupVideoTracking(media, campaignId);
        this.playVideo(videoUrl);
        this.fireImpressionTracking();

        // Evento de click na mídia para o Artemis
        artemisService.sendEvent('media-click', campaignId, media.requestId || '', media.uuid)
          .catch(err => logger.error('[VastPlayerPage] Erro ao enviar media-click:', err));

      } catch (error) {
        logger.error('[VastPlayerPage] Erro ao carregar VAST:', error);
        this.finish('error');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Overlay
  // ---------------------------------------------------------------------------

  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.id = 'vast-1st-view-overlay';
    this.overlay.innerHTML = this.buildHTML();
    document.body.appendChild(this.overlay);
    this.videoElement = this.overlay.querySelector('#vast-video') as HTMLVideoElement;
    this.attachOverlayEvents();
  }

  private buildHTML(): string {
    return `
      <style>
        #vast-1st-view-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          z-index: 9999;
          background: #000;
        }
        #vast-video {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          object-fit: contain;
          background: #000;
          pointer-events: none;
          user-select: none;
          -webkit-user-select: none;
        }
        #vast-click-overlay {
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          z-index: 5;
          cursor: default;
        }
        #vast-loading {
          position: absolute;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 50px; height: 50px;
          border: 5px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: white;
          animation: vast-spin 1s ease-in-out infinite;
        }
        @keyframes vast-spin {
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        #vast-skip-countdown,
        #vast-skip-btn {
          position: absolute;
          bottom: 20px; left: 20px;
          background: rgba(0, 0, 0, 0.55);
          color: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 2px;
          padding: 6px 14px;
          font-family: Arial, sans-serif;
          font-size: 13px;
          z-index: 10;
          pointer-events: auto;
        }
        #vast-skip-countdown {
          border: none;
          display: block;
        }
        #vast-skip-btn {
          cursor: pointer;
          display: none;
          background: rgba(0, 0, 0, 0.7);
          color: white;
        }
        #vast-saiba-mais {
          position: absolute;
          bottom: 20px; right: 20px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 2px;
          padding: 6px 12px;
          font-family: Arial, sans-serif;
          font-size: 13px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          cursor: pointer;
          z-index: 10;
          display: none;
          pointer-events: auto;
        }
        #vast-mute-btn {
          position: absolute;
          top: 16px; right: 16px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 50%;
          width: 36px; height: 36px;
          cursor: pointer;
          z-index: 10;
          display: none;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          pointer-events: auto;
        }
      </style>

      <video id="vast-video" autoplay muted playsinline disablePictureInPicture></video>
      <div id="vast-click-overlay"></div>
      <div id="vast-loading"></div>
      <span id="vast-skip-countdown">Pular em ${SKIP_DELAY_SECONDS}s</span>
      <button id="vast-skip-btn">Pular ›</button>
      <button id="vast-saiba-mais">Saiba mais</button>
      <button id="vast-mute-btn">🔇</button>
    `;
  }

  private attachOverlayEvents(): void {
    if (!this.overlay) return;

    const clickOverlay = this.overlay.querySelector('#vast-click-overlay') as HTMLElement;
    const skipBtn     = this.overlay.querySelector('#vast-skip-btn') as HTMLButtonElement;
    const saibaBtn    = this.overlay.querySelector('#vast-saiba-mais') as HTMLButtonElement;
    const muteBtn     = this.overlay.querySelector('#vast-mute-btn') as HTMLButtonElement;

    // Desmuta ao tocar na área do vídeo
    clickOverlay?.addEventListener('click', () => {
      if (!this.videoElement) return;
      if (this.videoElement.muted) {
        this.videoElement.muted = false;
        this.fireTrackingEvent('unmute');
        if (muteBtn) { muteBtn.textContent = '🔊'; }
      }
    });

    // Toggle mute
    muteBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.videoElement) return;
      this.videoElement.muted = !this.videoElement.muted;
      muteBtn.textContent = this.videoElement.muted ? '🔇' : '🔊';
      this.fireTrackingEvent(this.videoElement.muted ? 'mute' : 'unmute');
    });

    // Skip
    skipBtn?.addEventListener('click', () => {
      this.fireTrackingEvent('skip');
      AnalyticsUtil.track(ANALYTICS_EVENTS.FIRST_VIEW_SKIPPED, {
        campaignId: this.campaignId,
        mediaId: this.mediaId,
      });
      this.finish('skipped');
    });

    // Saiba mais (click-through)
    saibaBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.clickThroughUrl) return;
      this.clickTrackingUrls.forEach(url => this.sendTrackingRequest(url));
      AnalyticsUtil.track(ANALYTICS_EVENTS.FIRST_VIEW_CLICKTHROUGH, {
        campaignId: this.campaignId,
        mediaId: this.mediaId,
      });
      window.open(this.clickThroughUrl, '_blank');
    });

    // Previne context menu no vídeo
    this.overlay.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // ---------------------------------------------------------------------------
  // Video
  // ---------------------------------------------------------------------------

  private playVideo(url: string): void {
    if (!this.videoElement) return;

    this.videoElement.src = url;
    this.videoElement.load();
    this.videoElement.play().catch((err) => {
      if (err.name === 'NotAllowedError') {
        // Fallback: forçar mute e tentar de novo
        if (this.videoElement) {
          this.videoElement.muted = true;
          this.videoElement.play().catch(() => this.finish('error'));
        }
      } else {
        logger.error('[VastPlayerPage] Erro ao dar play no vídeo:', err);
        this.finish('error');
      }
    });
  }

  private setupVideoTracking(media: AAMedia, campaignId: string): void {
    if (!this.videoElement) return;

    // Start
    this.videoElement.addEventListener('playing', () => {
      this.fireTrackingEvent('start');
      this.showMuteButton();
      if (this.clickThroughUrl) this.showSaibaButton();
      this.startSkipCountdown();
      AnalyticsUtil.track(ANALYTICS_EVENTS.FIRST_VIEW_IMPRESSION, {
        campaignId,
        mediaId: media.uuid,
      });
    }, { once: true });

    // Quartiles
    this.videoElement.addEventListener('timeupdate', () => {
      if (!this.videoElement?.duration) return;
      const pct = (this.videoElement.currentTime / this.videoElement.duration) * 100;

      if (pct >= 25 && !this.quartilesFired.first) {
        this.quartilesFired.first = true;
        this.fireTrackingEvent('firstQuartile');
      }
      if (pct >= 50 && !this.quartilesFired.mid) {
        this.quartilesFired.mid = true;
        this.fireTrackingEvent('midpoint');
      }
      if (pct >= 75 && !this.quartilesFired.third) {
        this.quartilesFired.third = true;
        this.fireTrackingEvent('thirdQuartile');
      }
    });

    // Complete
    this.videoElement.addEventListener('ended', () => {
      this.quartilesFired.complete = true;
      this.fireTrackingEvent('complete');
      this.fireAllAdsCompleted();

      AnalyticsUtil.track(ANALYTICS_EVENTS.FIRST_VIEW_COMPLETED, {
        campaignId,
        mediaId: media.uuid,
      });

      this.finish('completed');
    });

    // Error
    this.videoElement.addEventListener('error', () => {
      this.fireTrackingEvent('error');
      this.errorUrls.forEach(url => this.sendTrackingRequest(url));
      AnalyticsUtil.track(ANALYTICS_EVENTS.FIRST_VIEW_ERROR, {
        campaignId,
        mediaId: media.uuid,
      });
      this.finish('error');
    });

    // Pause / Resume
    this.videoElement.addEventListener('pause', () => {
      if (!this.videoElement?.ended) this.fireTrackingEvent('pause');
    });
    this.videoElement.addEventListener('play', () => {
      if ((this.videoElement?.currentTime ?? 0) > 0) this.fireTrackingEvent('resume');
    });

    // Mute / Unmute via controles nativos
    this.videoElement.addEventListener('volumechange', () => {
      this.fireTrackingEvent(this.videoElement?.muted ? 'mute' : 'unmute');
    });
  }

  // ---------------------------------------------------------------------------
  // Skip countdown
  // ---------------------------------------------------------------------------

  private startSkipCountdown(): void {
    const countdownEl = this.overlay?.querySelector('#vast-skip-countdown') as HTMLElement;
    const skipBtn     = this.overlay?.querySelector('#vast-skip-btn') as HTMLElement;

    let remaining = SKIP_DELAY_SECONDS;

    const tick = () => {
      remaining--;
      if (remaining <= 0) {
        if (countdownEl) countdownEl.style.display = 'none';
        if (skipBtn) skipBtn.style.display = 'block';
      } else {
        if (countdownEl) countdownEl.textContent = `Pular em ${remaining}s`;
        window.setTimeout(tick, 1000);
      }
    };

    window.setTimeout(tick, 1000);
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  private hideLoading(): void {
    const el = this.overlay?.querySelector('#vast-loading') as HTMLElement;
    if (el) el.style.display = 'none';
  }

  private showMuteButton(): void {
    const el = this.overlay?.querySelector('#vast-mute-btn') as HTMLElement;
    if (el) el.style.display = 'flex';
  }

  private showSaibaButton(): void {
    const el = this.overlay?.querySelector('#vast-saiba-mais') as HTMLElement;
    if (el) el.style.display = 'block';
  }

  // ---------------------------------------------------------------------------
  // Finish / cleanup
  // ---------------------------------------------------------------------------

  private finish(result: VastPlayerResult): void {
    this.removeOverlay();
    this.resolveCallback?.(result);
    this.resolveCallback = null;
  }

  private removeOverlay(): void {
    if (this.overlay && document.body.contains(this.overlay)) {
      document.body.removeChild(this.overlay);
    }
    this.overlay = null;
    this.videoElement = null;
  }

  private resetState(): void {
    this.trackingEvents = {};
    this.impressionUrls = [];
    this.errorUrls = [];
    this.clickTrackingUrls = [];
    this.clickThroughUrl = null;
    this.quartilesFired = { first: false, mid: false, third: false, complete: false };
    this.allAdsCompletedFired = false;
  }

  // ---------------------------------------------------------------------------
  // VAST fetch & parse
  // ---------------------------------------------------------------------------

  private async fetchVast(url: string): Promise<Document | null> {
    try {
      const response = await fetch(url, { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        logger.error('[VastPlayerPage] VAST fetch falhou, status:', response.status);
        return null;
      }
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/xml');
    } catch (err) {
      logger.error('[VastPlayerPage] Erro ao buscar VAST:', err);
      return null;
    }
  }

  private extractTrackingEvents(xml: Document): void {
    const trackingEls = xml.getElementsByTagName('Tracking');
    for (let i = 0; i < trackingEls.length; i++) {
      const event = trackingEls[i].getAttribute('event');
      const url   = trackingEls[i].textContent?.trim();
      if (!event || !url) continue;
      if (!this.trackingEvents[event]) this.trackingEvents[event] = [];
      this.trackingEvents[event].push(url);
    }
    logger.debug('[VastPlayerPage] Tracking events extraídos:', Object.keys(this.trackingEvents));
  }

  private extractImpressionUrls(xml: Document): void {
    const els = xml.getElementsByTagName('Impression');
    for (let i = 0; i < els.length; i++) {
      const url = els[i].textContent?.trim();
      if (url) this.impressionUrls.push(url);
    }
  }

  private extractErrorUrls(xml: Document): void {
    const els = xml.getElementsByTagName('Error');
    for (let i = 0; i < els.length; i++) {
      const url = els[i].textContent?.trim();
      if (url) this.errorUrls.push(url);
    }
  }

  private extractClickInfo(xml: Document): void {
    const clickThroughs = xml.getElementsByTagName('ClickThrough');
    if (clickThroughs.length > 0) {
      this.clickThroughUrl = clickThroughs[0].textContent?.trim() || null;
    }
    const clickTrackings = xml.getElementsByTagName('ClickTracking');
    for (let i = 0; i < clickTrackings.length; i++) {
      const url = clickTrackings[i].textContent?.trim();
      if (url) this.clickTrackingUrls.push(url);
    }
  }

  private extractMediaFileUrl(xml: Document): string | null {
    const mediaFiles = xml.getElementsByTagName('MediaFile');
    let fallback: string | null = null;

    for (let i = 0; i < mediaFiles.length; i++) {
      const type = mediaFiles[i].getAttribute('type') || '';
      const url  = mediaFiles[i].textContent?.trim() || '';

      if (type.includes('mp4') || url.endsWith('.mp4')) return url;
      if (!fallback && (type.includes('video') || /\.(mp4|webm|ogg|mov)$/i.test(url))) {
        fallback = url;
      }
    }

    return fallback;
  }

  // ---------------------------------------------------------------------------
  // Tracking
  // ---------------------------------------------------------------------------

  private fireImpressionTracking(): void {
    this.impressionUrls.forEach(url => this.sendTrackingRequest(url));
  }

  private fireTrackingEvent(eventName: string): void {
    const urls = this.trackingEvents[eventName];
    if (urls) urls.forEach(url => this.sendTrackingRequest(url));
  }

  /**
   * Replica o comportamento do IMA SDK: dispara o primeiro URL de "complete"
   * com o parâmetro type trocado para "all-ads-completed".
   */
  private fireAllAdsCompleted(): void {
    if (this.allAdsCompletedFired) return;
    this.allAdsCompletedFired = true;

    const completeUrls = this.trackingEvents['complete'];
    if (!completeUrls?.length) return;

    const url = completeUrls[0];
    const allAdsUrl = this.replaceTypeParam(url, 'ad-completed', 'all-ads-completed');
    this.sendTrackingRequest(allAdsUrl);
  }

  private replaceTypeParam(url: string, from: string, to: string): string {
    try {
      const [base, qs] = url.split('?');
      const params = new URLSearchParams(qs || '');
      if (params.get('type') === from) params.set('type', to);
      else params.set('type', to);
      return `${base}?${params.toString()}`;
    } catch {
      return url;
    }
  }

  private sendTrackingRequest(url: string): void {
    if (!url) return;
    fetch(url, { method: 'GET', mode: 'no-cors' }).catch(() => {});
  }
}
